import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import {
   AllowedIpAddressInput,
   BlockedIpAddressInput,
   BlockedIpStatuses,
   OrderingOption,
} from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DateTimeFilterFormatPipe } from 'src/app/pipes/datetime-filter-format-pipe/datetime-filter-format-pipe';
import { AllowBannedIpRequest } from '../models/allow-banned-ip.model';
import { BannedProvisioningIpFilterOption } from '../models/banned-provisioning-ip-filter-option';
import {
   BannedProvisioningIpOrderingOptions,
   BannedProvisioningIpRegisterOptions,
} from '../models/banned-provisioning-ip-register-options';
import { getBannedProvisioningIpsQuery } from '../queries/bannedprovisioningips.graphql';
import {
   GetBannedProvisioningIps,
   GetBannedProvisioningIpsVariables,
} from '../queries/generated/GetBannedProvisioningIps';
import { SaveAllowedIpAddress, SaveAllowedIpAddressVariables } from '../queries/generated/SaveAllowedIpAddress';
import { SaveBlockedIpAddress, SaveBlockedIpAddressVariables } from '../queries/generated/SaveBlockedIpAddress';
import { saveAllowedIpAddressMutation } from '../queries/save-allowed-ip-address.graphql';
import { saveBlockedIpAddressMutation } from '../queries/save-blocked-ip-address.graphql';

@Injectable({ providedIn: 'root' })
export class BannedProvisioningIpRegisterApiService {
   private dateTimePipe: DateTimeFilterFormatPipe;

   constructor(private apiService: LcsApiService) {
      this.dateTimePipe = new DateTimeFilterFormatPipe();
   }

   getBannedProvisioningIps(
      endCursor: string | null,
      filters: BannedProvisioningIpFilterOption[] | null,
      quickFilters: BannedProvisioningIpRegisterOptions | null,
      orderingOptions: BannedProvisioningIpOrderingOptions[] | null
   ): Observable<ExecutionResult<GetBannedProvisioningIps>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertBannedIpFilterOptions.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.type) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'Type',
               operation: FilterOperations.EqualTo,
               value: quickFilters.type,
            });
         }

         if (quickFilters.status) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'Status',
               operation: FilterOperations.EqualTo,
               value: quickFilters.status,
            });
         }

         if (quickFilters.startDate) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'FirstSeen',
               operation: FilterOperations.GreaterThanOrEqualTo,
               value: this.dateTimePipe.transform(new Date(quickFilters.startDate)),
            });
         }

         if (quickFilters.endDate) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'FirstSeen',
               operation: FilterOperations.LessThanOrEqualTo,
               value: this.dateTimePipe.transform(new Date(quickFilters.endDate)),
            });
         }

         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = {
               filterOptions: [],
               logicalOperator: LogicalOperators.Or,
            };
            subExpression.filterOptions.push({
               field: 'IpAddress',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }
      const variables = this.getBannedProvisioningIpsVariables(
         endCursor,
         filterBuilderFilterExpression,
         orderingOptions
      );
      return this.apiService.query<GetBannedProvisioningIps, GetBannedProvisioningIpsVariables>({
         query: getBannedProvisioningIpsQuery,
         variables,
      });
   }

   allowIpAddress(allowedIpAddress: AllowBannedIpRequest): Observable<ExecutionResult<SaveAllowedIpAddress>> {
      const fields: (keyof AllowedIpAddressInput)[] = [
         'ipAddress',
         'accountId',
         'qManageTicketId',
         'originatingType',
      ];

      return this.apiService.query<SaveAllowedIpAddress, SaveAllowedIpAddressVariables>({
         query: saveAllowedIpAddressMutation,
         variables: {
            allowedIpAddress: {
               ...allowedIpAddress,
               fields,
            },
         },
      });
   }

   unbanBlockedIpAddress(blockedIpAddressId: number): Observable<ExecutionResult<SaveBlockedIpAddress>> {
      const fields: (keyof BlockedIpAddressInput)[] = ['status'];

      return this.apiService.query<SaveBlockedIpAddress, SaveBlockedIpAddressVariables>({
         query: saveBlockedIpAddressMutation,
         variables: {
            blockedIpAddress: {
               blockedIpAddressId,
               fields,
               status: BlockedIpStatuses.Unbanning,
            },
         },
      });
   }

   private getBannedProvisioningIpsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetBannedProvisioningIpsVariables {
      const variables: GetBannedProvisioningIpsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private convertBannedIpFilterOptions(option: BannedProvisioningIpFilterOption): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: BannedProvisioningIpFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
      return null;
   }
}
